<template>
  <div
    id="products"
    class="bg-transparent flex flex-col md:flex-row gap-8 md:gap-6"
  >
    <call-products
      id="calls"
      :class="[
        transition ? 'navigate' : 'bg-grayscale-10',
        feedbackPrice ? 'md:w-1/2' : '',
        'rounded-8 overflow-hidden border border-grayscale-20',
      ]"
      v-if="callPrice"
      :pro-name="proName"
      :pro="pro"
      :price="cheapestCall!.price"
      @request="requestCall"
    ></call-products>
    <feedback-products
      id="feedbacks"
      :class="[
        transition ? 'navigate' : 'bg-grayscale-10',
        callPrice ? 'md:w-1/2' : '',
        'rounded-8 overflow-hidden border border-grayscale-20',
      ]"
      v-if="feedbackPrice"
      :price="cheapestFeedback!.price"
      :pro="pro"
      @request="requestFeedback"
    ></feedback-products>
    <XposureLoader
      v-if="loading"
      additionalClasses="w-full h-500px md:h-300px"
      spinnerAdditionalClasses="fixed top-[50vh]"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import type { PropType } from "vue";
import type { User } from "@/types/user";
import { ProductNameByType, ProductType } from "@/types/product";
import type { Product } from "@/types/product";
import { useProductsStore } from "@/store/products";
import FeedbackProducts from "./FeedbackProducts.vue";
import CallProducts from "./CallProducts.vue";
import { getDisplayName } from "@/utils/display-name";
import { useGtm } from "@gtm-support/vue-gtm";
import { useAuthenticationStore } from "@/store/authentication";
import AnalyticsService from "@/services/analytics-service";
import XposureLoader from "@/components/loader/XposureLoader.vue";

const router = useRouter();

const gtm = useGtm();

const props = defineProps({
  pro: {
    type: Object as PropType<User>,
    required: true,
  },
  feedbackPrice: {
    type: String,
  },
  callPrice: {
    type: String,
  },
  playTransition: {
    type: Boolean,
    default: false,
  },
  topButtonRequest: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
  },
});

const transition = computed(() => props.playTransition);
const proName = computed(() => getDisplayName(props.pro));

const productPrices = ref({
  audio: "",
  free: "",
  longCall: "",
  shortCall: "",
  video: "",
  written: "",
});

const prodList = ref<(Product & { title: ProductNameByType })[]>([]);
const productsStore = useProductsStore();
const authStore = useAuthenticationStore();
const analyticsService = new AnalyticsService("analytics");

const setPrices = () => {
  if (props.pro.products) {
    prodList.value = props.pro.products
      .filter((product) => product.isAvailable === true)
      .map((product) => {
        const obj = { [product.type]: product.price.toString() };
        Object.assign(productPrices.value, obj);
        return {
          ...product,
          title: ProductNameByType[product.type],
          proName: props.pro.uniqueName,
        };
      });
  }
};

watch(
  () => props.topButtonRequest,
  () => {
    if (props.topButtonRequest) {
      request(props.topButtonRequest);
    }
  },
);

const calls = props.pro.products?.filter(
  (product) =>
    [ProductType.shortCall, ProductType.longCall].includes(product.type) &&
    product.isAvailable,
);
const cheapestCall = calls?.sort((a, b) => a.price - b.price)[0];

const feedbacks = props.pro.products?.filter(
  (product) =>
    [ProductType.audio, ProductType.video, ProductType.written].includes(
      product.type,
    ) && product.isAvailable,
);
const cheapestFeedback = feedbacks?.sort((a, b) => a.price - b.price)[0];

const requestCall = () => {
  request(cheapestCall?.type!);
};

const requestFeedback = () => {
  request(cheapestFeedback?.type!);
};

const request = (type: string) => {
  const index = computed(() =>
    prodList.value.findIndex((product) => product.type === type),
  );
  const selectedProduct = ref<Product & { title: ProductNameByType }>(
    prodList.value![index.value],
  );
  productsStore.setPurchaseProduct(selectedProduct.value);
  if (selectedProduct.value.type !== ProductType.free) {
    gtm?.trackEvent({
      event: "begin_checkout",
      ecommerce: {
        items: [
          {
            item_id: selectedProduct.value._id,
            item_name: selectedProduct.value.proName,
            item_category: selectedProduct.value.type,
            currency: "USD",
            value: selectedProduct.value.price,
            quantity: 1,
          },
        ],
      },
    });

    selectedProduct.value.checkoutId =
      Date.now().toString(36) + Math.random().toString(36).substr(2);

    productsStore.setPurchaseProductCheckoutId(
      selectedProduct.value.checkoutId,
    );

    analyticsService.trackButtonClicked(
      "Request",
      "Pro Profile Page",
      "Purchase Flow",
      Boolean(authStore.authUser?.authToken),
    );
  }
  router.push(`/purchase/${selectedProduct.value.proName}`);
};

onMounted(async () => {
  setPrices();
});
</script>
<style>
.navigate {
  transition: background-color 0.3s ease-in-out;
  background-color: #2e2e33;
}
</style>
